import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Sakura.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;

const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

/* ── Scroll-reveal hook ──────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.sak-reveal, .sak-reveal-left, .sak-reveal-right';

    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('sak-in'));
      return;
    }

    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('sak-in');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ── Cherry blossom petals ───────────────────────────────────── */
const PETALS = [
  { left: '5%',  delay: '0s',    dur: '7s'  },
  { left: '12%', delay: '1.2s',  dur: '8.5s' },
  { left: '23%', delay: '2.4s',  dur: '6.8s' },
  { left: '35%', delay: '0.5s',  dur: '9s'  },
  { left: '47%', delay: '3.1s',  dur: '7.4s' },
  { left: '58%', delay: '1.8s',  dur: '8s'  },
  { left: '67%', delay: '0.9s',  dur: '7.2s' },
  { left: '75%', delay: '2.7s',  dur: '9.5s' },
  { left: '83%', delay: '4.0s',  dur: '6.5s' },
  { left: '91%', delay: '1.5s',  dur: '8.2s' },
];

function SakuraPetals() {
  return (
    <div className="sak-petals" aria-hidden="true">
      {PETALS.map((p, i) => (
        <span
          key={i}
          className="sak-petal"
          style={{
            left: p.left,
            animationDelay: p.delay,
            animationDuration: p.dur,
          }}
        />
      ))}
    </div>
  );
}

/* ── Countdown ───────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms % 86400000 / 3600000),
        m: Math.floor(ms % 3600000 / 60000),
        s: Math.floor(ms % 60000 / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);

  return (
    <div className="sak-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="sak-cu">
          <span className="sak-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="sak-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Calendar ────────────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++)    cells.push({ n: i, cur: true });
  while (cells.length < 42)         cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  return (
    <div className="sak-calendar">
      <div className="sak-cal-header">
        <span style={{ color: accent }}>{MON[mo]}</span>
        <span className="sak-cal-year">{yr}</span>
      </div>
      <div className="sak-cal-grid">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(h => (
          <div key={h} className="sak-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isWed = c.cur && c.n === day;
          return (
            <div key={i}
              className={`sak-cal-cell${!c.cur ? ' sak-cal-muted' : ''}${isWed ? ' sak-cal-today' : ''}`}
              style={isWed ? { background: accent, color: '#fff' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVP Form ───────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus] = useState('confirmed');
  const [name, setName] = useState('');
  const [survey, setSurvey] = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="sak-rsvp-done" style={{ borderColor: accent }}>
      <div className="sak-rsvp-done-icon">🌸</div>
      <strong style={{ color: accent }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="sak-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="sak-rsvp-options">
        {[
          { v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' },
          { v: 'declined',  l: bc.rsvp?.noText  || 'Не смогу прийти' },
        ].map(o => (
          <label key={o.v}
            className={`sak-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#fff', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="sak-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="sak-survey-q">
          <p className="sak-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="sak-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="sak-survey-opts">{(q.options || []).map(opt => {
                const sel = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`sak-rsvp-opt sak-rsvp-opt-sm${sel ? ' active' : ''}`}
                    style={sel ? { background: accent, color: '#fff', borderColor: accent } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={sel}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="sak-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`sak-rsvp-opt sak-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#fff', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p className="sak-field-label">{bc.message.fieldLabel}</p>
          )}
          <textarea className="sak-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}

      <button type="submit" className="sak-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ────────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="sak-wishlist">
      {items.map(item => (
        <div key={item.id} className="sak-wish-item">
          <div className="sak-wish-info">
            <div className="sak-wish-name">{item.name}</div>
            {item.price && <div className="sak-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="sak-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="sak-btn-outline">
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="sak-btn sak-btn-reserved"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="sak-btn" style={{ background: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Section Title ───────────────────────────────────────────── */
function SakSection({ id, label, children, className = '' }) {
  return (
    <section className={`sak-section ${className}`} id={id}>
      <div className="sak-section-header sak-reveal">
        <div className="sak-section-label">{label}</div>
        <div className="sak-section-rule" />
      </div>
      {children}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export default function Sakura({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks  = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || '#e8657a';

  const fontTitle = project.fonts?.title || null;
  const fontBody  = project.fonts?.body  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-title': `'${fontTitle}', 'Noto Serif', serif`       } : {}),
    ...(fontBody  ? { '--f-body':  `'${fontBody}',  'Inter', sans-serif` } : {}),
  };

  /* Dynamic Google Fonts */
  useEffect(() => {
    const id = 'sak-gfonts';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id  = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const families = [fontTitle, fontBody].filter(Boolean);
    const baseQuery = "family=Noto+Serif:ital,wght@0,300;0,400;0,700;1,300;1,400&family=Inter:wght@300;400;500";
    const extraQuery = families.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${baseQuery}${extraQuery ? '&' + extraQuery : ''}&display=swap`;
  }, [fontTitle, fontBody]);

  /* Photos */
  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const p1        = photos[0];
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || photos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride  = project.bride || 'Невеста';
  const groom  = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_NAV       = blocks.includes('menu');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  /* Google Calendar helper */
  const calAddBtn = HAS_CALENDAR ? (() => {
    const d   = new Date(weddingDate);
    const pad = n => String(n).padStart(2, '0');
    const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
    const href  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
        className="sak-cal-add-btn" style={{ background: accent }}>
        🌸 {bc.calendar?.title || 'Добавить в календарь'}
      </a>
    );
  })() : null;

  return (
    <div
      className="sak-page"
      ref={pageRef}
      style={{ '--sak-accent': accent, ...fontStyle }}
      data-container="sakura"
    >
      {/* Falling petals — always decorative */}
      <SakuraPetals />

      {/* ══ NAV ══════════════════════════════════════════════════ */}
      {HAS_NAV && (
        <nav className="sak-nav">
          <span className="sak-nav-brand">{bride} &amp; {groom}</span>
          <div className="sak-nav-line" />
          <div className="sak-nav-links">
            {HAS_SCHEDULE  && <a href="#schedule">Тайминг</a>}
            {HAS_MAP       && <a href="#map">Локация</a>}
            {HAS_DRESSCODE && <a href="#dresscode">Дресс-код</a>}
            {HAS_RSVP      && <a href="#rsvp" className="sak-nav-cta" style={{ color: accent }}>Анкета</a>}
          </div>
        </nav>
      )}

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <section className="sak-hero">
        {p1 && (
          <div className="sak-hero-photo">
            <img src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
          </div>
        )}
        <div className="sak-hero-overlay" />

        <div className="sak-hero-content">
          <p className="sak-hero-slogan">
            {bc.hero?.subtitle || project.wedding_slogan || 'Вы приглашены'}
          </p>

          <div className="sak-hero-names">
            <h1 className="sak-name sak-name-bride">{bride}</h1>
            <div className="sak-name-divider">
              <span className="sak-name-line" />
              <span className="sak-name-amp">❤</span>
              <span className="sak-name-line" />
            </div>
            <h1 className="sak-name sak-name-groom">{groom}</h1>
          </div>

          <div className="sak-hero-date" style={{ color: accent }}>
            {fmtDate(weddingDate)}
            {project.wedding_time && (
              <span className="sak-hero-time"> · {project.wedding_time}</span>
            )}
          </div>

          {HAS_RSVP && (
            <a href="#rsvp" className="sak-hero-btn" style={{ borderColor: accent, color: accent }}>
              {bc.rsvp?.yesText || 'Заполнить анкету'}
            </a>
          )}
        </div>
      </section>

      {/* ══ INVITATION ════════════════════════════════════════════ */}
      <SakSection id="invitation" label={bc.message?.title || 'Дорогие гости'}>
        <div className="sak-inv-grid">
          <div className="sak-inv-text sak-reveal-left">
            {bc.message?.text
              ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
              : (
                <>
                  <p>С трепетом и радостью мы приглашаем вас разделить с нами <strong>самый важный день</strong> нашей жизни.</p>
                  <p>В этот день наши сердца наполнятся любовью, а воспоминания останутся навсегда.</p>
                  <p>Пожалуйста, подтвердите своё присутствие, заполнив <strong style={{ color: accent }}>форму ниже</strong>.</p>
                </>
              )
            }
          </div>

          <div className="sak-inv-photos sak-reveal-right">
            {invP1 ? (
              <div className="sak-inv-photo sak-inv-photo--a">
                <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
              </div>
            ) : (
              <div className="sak-inv-photo sak-inv-photo--a sak-inv-photo--empty">
                <span>Ваше фото</span>
              </div>
            )}
            {invP2 ? (
              <div className="sak-inv-photo sak-inv-photo--b">
                <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
              </div>
            ) : (
              <div className="sak-inv-photo sak-inv-photo--b sak-inv-photo--empty">
                <span>Ещё фото</span>
              </div>
            )}
          </div>
        </div>

        <div className="sak-reveal" style={{ '--d': '0.1s' }}>
          <CalendarWidget date={weddingDate} accent={accent} addBtn={calAddBtn} />
        </div>
      </SakSection>

      <div className="sak-separator" />

      {/* ══ COUNTDOWN ═════════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <>
          <SakSection id="countdown" label={bc.countdown?.title || 'До торжества осталось'}>
            <BlockSubtitle text={bc.countdown?.subtitle} className="sak-section-sub sak-reveal" />
            <div className="sak-reveal" style={{ '--d': '0.15s' }}>
              <Countdown date={weddingDate} accent={accent} />
            </div>
          </SakSection>
          <div className="sak-separator" />
        </>
      )}

      {/* ══ SCHEDULE ══════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <>
          <SakSection id="schedule" label={bc.schedule?.title || 'Программа дня'}>
            <BlockSubtitle text={bc.schedule?.subtitle} className="sak-section-sub sak-reveal" />
            <div className="sak-schedule">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <div className="sak-sched-group">
                      <div className="sak-sched-group-title" style={{ color: accent }}>Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div key={i} className="sak-sched-item sak-reveal" style={{ '--d': `${i * 0.08}s` }}>
                          <div className="sak-sched-time" style={{ color: accent }}>{item.time}</div>
                          <div className="sak-sched-dot" style={{ background: accent }} />
                          <div className="sak-sched-info">
                            <div className="sak-sched-event">{item.event}</div>
                            {item.description && <div className="sak-sched-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="sak-sched-group">
                    {optional.length > 0 && (
                      <div className="sak-sched-group-title" style={{ color: accent }}>Основная программа</div>
                    )}
                    {mainItems.map((item, i) => (
                      <div key={i} className="sak-sched-item sak-reveal" style={{ '--d': `${i * 0.08}s` }}>
                        <div className="sak-sched-time" style={{ color: accent }}>{item.time}</div>
                        <div className="sak-sched-dot" style={{ background: accent }} />
                        <div className="sak-sched-info">
                          <div className="sak-sched-event">{item.event}</div>
                          {item.description && <div className="sak-sched-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="sak-sched-placeholder">
                  {[['14:00', 'Сбор гостей'], ['15:00', 'Церемония'], ['16:00', 'Фотосессия'], ['17:00', 'Банкет']].map(([t, e], i) => (
                    <div key={i} className="sak-sched-item" style={{ opacity: 0.4 }}>
                      <div className="sak-sched-time">{t}</div>
                      <div className="sak-sched-dot" />
                      <div className="sak-sched-event">{e}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SakSection>
          <div className="sak-separator" />
        </>
      )}

      {/* ══ MAP ═══════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <>
          <SakSection id="map" label={bc.map?.title || 'Место проведения'}>
            <BlockSubtitle text={bc.map?.subtitle} className="sak-section-sub sak-reveal" />
            <div className="sak-venues">
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="sak-venue sak-reveal" style={{ '--d': `${i * 0.1}s` }}>
                  <div className="sak-venue-icon" style={{ color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="sak-venue-info">
                    {v.label && <span className="sak-venue-label" style={{ color: accent }}>{v.label}</span>}
                    {v.name  && <span className="sak-venue-name">{v.name}</span>}
                    <span className="sak-venue-addr">{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer" className="sak-btn-outline">
                    Маршрут
                  </a>
                </div>
              )) : project.wedding_address ? (
                <div className="sak-venue sak-reveal">
                  <div className="sak-venue-icon" style={{ color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <span className="sak-venue-addr">{project.wedding_address}</span>
                </div>
              ) : (
                <p className="sak-placeholder-text">Адрес будет добавлен на шаге «Данные»</p>
              )}
            </div>

            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="sak-map-frame sak-reveal" style={{ '--d': '0.2s' }}>
              {(() => {
                if (project.map_url) {
                  return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const pts = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const cx  = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const cy  = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  return (
                    <MapFrame region={project.region} title="Карта" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }}
                      src={`https://yandex.ru/map-widget/v1/?pt=${pts}&ll=${cx},${cy}&z=14&l=map`} />
                  );
                }
                const addr = venues[0]?.address || project.wedding_address;
                if (addr) {
                  return (
                    <MapFrame region={project.region} title="Карта" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }}
                      src={`https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=15&l=map`} />
                  );
                }
                return (
                  <div className="sak-map-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill={accent + '60'}>
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    </svg>
                    <span>Карта появится после добавления адреса</span>
                  </div>
                );
              })()}
            </div>
            )}
          </SakSection>
          <div className="sak-separator" />
        </>
      )}

      {/* ══ DRESSCODE ═════════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <>
          <SakSection id="dresscode" label={bc.dresscode?.title || 'Дресс-код'}>
            <BlockSubtitle text={bc.palette?.subtitle} className="sak-section-sub sak-reveal" />
            {project.dresscode_palette?.length > 0 && (
              <div className="sak-palette sak-reveal">
                {project.dresscode_palette.map((c, i) => (
                  <div key={i} className="sak-swatch-wrap">
                    <div className="sak-swatch" style={{ background: c.hex || c }} />
                    {c.name && <span className="sak-swatch-name">{c.name}</span>}
                  </div>
                ))}
              </div>
            )}
            {dcPhotos.length > 0 && (
              <div className="sak-dc-photos sak-reveal" style={{ '--d': '0.1s' }}>
                {dcPhotos.slice(0, 3).map((ph, i) => (
                  <div key={i} className="sak-dc-photo">
                    <img src={src(ph.url)} alt="" style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                  </div>
                ))}
              </div>
            )}
          </SakSection>
          <div className="sak-separator" />
        </>
      )}

      {/* ══ RSVP ══════════════════════════════════════════════════ */}
      {HAS_RSVP && (
        <>
          <SakSection id="rsvp" label={bc.rsvp?.title || 'Подтверждение'}>
            <BlockSubtitle text={bc.rsvp?.subtitle} className="sak-section-sub sak-reveal" />
            <div className="sak-reveal" style={{ '--d': '0.1s' }}>
              <RSVPForm project={project} accent={accent} />
            </div>
          </SakSection>
          <div className="sak-separator" />
        </>
      )}

      {/* ══ WISHLIST ══════════════════════════════════════════════ */}
      {HAS_WISHLIST && (
        <>
          <SakSection id="wishlist" label={bc.wishlist?.title || 'Вишлист'}>
            <BlockSubtitle text={bc.wishlist?.subtitle} className="sak-section-sub sak-reveal" />
            <div className="sak-reveal" style={{ '--d': '0.1s' }}>
              <Wishlist project={project} accent={accent} />
            </div>
          </SakSection>
          <div className="sak-separator" />
        </>
      )}

      {/* ══ FOOTER ════════════════════════════════════════════════ */}
      <footer className="sak-footer">
        <div className="sak-footer-names" style={{ color: accent }}>
          {bride} &amp; {groom}
        </div>
        <div className="sak-footer-date">{fmtDate(weddingDate)}</div>
        <div className="sak-footer-petal" aria-hidden="true">🌸</div>
      </footer>

      {/* ══ WATERMARK (free plan) ═════════════════════════════════ */}
      {project.payment_plan === 'free' && (
        <div className="sak-watermark">
          Создано на <a href="/" style={{ color: accent }}>WeddingInvite</a>
        </div>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
